/**
 * **「本次会话里用户确认过的位置」集合**（2026-10-20 第七轮）。
 *
 * ## 它是什么
 *
 * 「按书录入」的预览里，用户点字 → 浮窗 → 「✅ 确认就是「dé」」/ 换一个读音，
 * 都在做同一件事：**给这个位置一个明确的读音**。这份集合记的就是这些位置
 * （键 = `行号:这一行第几个字`，值 = 确认下来的读音）：
 *
 *   · 预览请求里作为 `readings` 覆盖发出去（服务层把它当"已确认"，
 *     文件里标了 `?` 的位置于是不再拦保存）；
 *   · 提交请求里**原样再发一次**（预览与提交是同一份输入 + 同一份确认集合）；
 *   · **改文本 / 换表 / 重置时清空**（行号与位置全变了，旧确认不能复用）。
 *
 * ## 为什么必须单独抽出来（本轮修掉的一个真 bug）
 *
 * 改造前，`runListPreview()` 结束后会把报告里**所有有读音的位置**都回填进
 * 这个集合（包括文件自带的、连 `?` 都没确认的那些）。于是用户只要点**一个**
 * 确认按钮、重新预览一次，第二份请求就会把**满屏的黄卡**都当成"用户确认过" ——
 * 颜色一起变绿，而且能直接保存。用户的原话是"确认过的状态要持久"，不是
 * "确认一个等于确认全部"。
 *
 * 所以这里把语义钉死：**集合里只放用户真的动过的位置**（`syncConfirmedReadings`
 * 只同步 `prev` 里已有的键，绝不新增）。纯函数、可单测（本仓 web 测试没有 jsdom）。
 */

/** 一个位置在集合里的键（`第 3 行第 2 个字` → `"3:2"`）。 */
export function confirmedReadingKey(line: number, indexInLine: number): string {
  return `${line}:${indexInLine}`;
}

/** 把集合里的一条写成请求体里的 `readings` 覆盖项（空集合 = `[]`）。 */
export interface ImportReadingOverridePayload {
  line: number;
  index: number;
  reading: string;
}

/**
 * 集合 → 请求体里的 `readings`（顺序 = 对象插入顺序）。
 *
 * ⚠️ 键解析不出来 / 行号或字序不是正整数的一律丢掉（不编一个 0 号的假位置）。
 */
export function confirmedReadingsPayload(
  overrides: Readonly<Record<string, string>>,
): ImportReadingOverridePayload[] {
  return Object.entries(overrides)
    .map(([key, reading]) => {
      const [lineText, indexText] = key.split(':');
      return { line: Number(lineText), index: Number(indexText), reading };
    })
    .filter(
      (entry) =>
        Number.isInteger(entry.line) &&
        entry.line > 0 &&
        Number.isInteger(entry.index) &&
        entry.index > 0 &&
        entry.reading !== '',
    );
}

/** 用户确认了一个位置：返回**新对象**（不原地改，React 才能看见变化）。 */
export function rememberConfirmedReading(
  overrides: Readonly<Record<string, string>>,
  line: number,
  indexInLine: number,
  reading: string,
): Record<string, string> {
  return { ...overrides, [confirmedReadingKey(line, indexInLine)]: reading };
}

/** 同步时只要这些字段（`ListingPositionInfo` 的一小部分）。 */
export interface ConfirmedReadingPosition {
  line: number;
  indexInLine: number;
  reading: string;
}

/**
 * **重新预览之后**把集合与报告对齐：只同步集合里**已经存在**的键，
 * ⛔ **绝不**把报告里"文件自带的读音"塞进来。
 *
 * 规则：
 *   · `prev` 为空 ⇒ 原样返回（同一个对象引用，避免无意义的重渲染）；
 *   · 集合里的某个位置在报告里 ⇒ 用报告里的读音覆盖（后端可能归一过，
 *     例如 `shuang1` → `shuāng`），但**只有报告里那一处确实有读音**才覆盖；
 *   · 报告里没有的位置（文本变了 / 被截断）⇒ 保留用户刚选的值（更安全）；
 *   · 报告里**有读音、但集合里没有**的位置 ⇒ **一概不加**（那正是满屏变绿的 bug）。
 */
export function syncConfirmedReadings(
  prev: Readonly<Record<string, string>>,
  positions: readonly ConfirmedReadingPosition[],
): Record<string, string> {
  if (Object.keys(prev).length === 0) return prev as Record<string, string>;

  const next: Record<string, string> = {};
  for (const position of positions) {
    const key = confirmedReadingKey(position.line, position.indexInLine);
    const picked = prev[key];
    if (picked === undefined) continue;
    next[key] = position.reading !== '' ? position.reading : picked;
  }
  /** 保留用户刚选、但报告里还没体现的（理论上不会有；留着更安全）。 */
  for (const [key, value] of Object.entries(prev)) {
    if (next[key] === undefined) next[key] = value;
  }
  return next;
}
